<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Search, X, CalendarDays, CalendarClock, Ban } from 'lucide-vue-next';
import { useVisits } from '../composables/useVisits';
import VisitRescheduleModal from '../components/VisitRescheduleModal.vue';
import VisitCancelDialog from '../components/VisitCancelDialog.vue';
import { asDayValue, formatVisitAt } from '../utils/date';
import type { Visit } from '../services/api';
import { serviceNames } from '../utils/visit';

const router = useRouter();
const { t } = useI18n();
const { visits, isLoading, error, currentPage, hasMore, fetchVisits, clearVisits } = useVisits();

const searchQuery = ref('');
const dateFrom = ref('');
const dateTo = ref('');

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

const applyFilters = (delay: number) => {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    visits.value = [];
    fetchVisits(1, 10, {
      search: searchQuery.value || undefined,
      date_from: dateFrom.value || undefined,
      date_to: dateTo.value || undefined,
    });
  }, delay);
};

// Typing is debounced; picking a date or hitting "today" is a discrete action
// that should not sit behind a delay. Both paths share one timer, so a pending
// keystroke fetch is replaced rather than raced.
watch(searchQuery, () => applyFilters(300));
watch([dateFrom, dateTo], () => applyFilters(0));

const clearSearch = () => {
  searchQuery.value = '';
};

const isTodayActive = computed(() => {
  const today = asDayValue(new Date());
  return dateFrom.value === today && dateTo.value === today;
});

const toggleToday = () => {
  if (isTodayActive.value) {
    dateFrom.value = '';
    dateTo.value = '';
    return;
  }

  const today = asDayValue(new Date());
  dateFrom.value = today;
  dateTo.value = today;
};

const handleLoadMore = () => {
  if (hasMore.value && !isLoading.value) {
    fetchVisits(currentPage.value + 1);
  }
};

const userName = (visit: Visit): string => {
  if (!visit.user) return '—';
  return `${visit.user.first_name} ${visit.user.last_name}`.trim();
};

// Only a visit that has not happened yet can be moved or called off. Not reactive
// to the passing of time — the backend rejects a past date anyway.
const isUpcoming = (visit: Visit): boolean => new Date(visit.visit_at).getTime() > Date.now();

const rescheduleTarget = ref<Visit | null>(null);
const cancelTarget = ref<Visit | null>(null);

onMounted(() => {
  clearVisits();
  fetchVisits(1);
});

onUnmounted(() => {
  if (debounceTimer) clearTimeout(debounceTimer);
});
</script>

<template>
  <div class="flex flex-col min-h-full bg-[#0A0A0B]">
    <div class="flex-1 p-4 md:p-6 lg:p-8">
      <div class="max-w-7xl mx-auto">
        <!-- Page Header -->
        <div class="mb-6">
          <h2 class="text-2xl font-bold text-[#F5F0EA]">
            {{ t('visits.title') }}
          </h2>
          <p class="text-[#524840] mt-1">{{ t('visits.subtitle') }}</p>
        </div>

        <!-- Filters: one row on desktop, wraps on narrow screens -->
        <div class="mb-4 flex flex-wrap items-center gap-3">
          <div class="relative flex-1 min-w-[12rem]">
            <Search
              class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#524840] pointer-events-none"
            />
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="t('visits.search_placeholder')"
              class="w-full pl-9 pr-9 py-2.5 bg-[#111113] border border-[#2A2018] rounded-xl text-sm text-[#F5F0EA] placeholder-[#9E9080] focus:outline-none focus:ring-2 focus:ring-teal-400 transition"
            />
            <button
              v-if="searchQuery"
              @click="clearSearch"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-[#524840] hover:text-[#F5F0EA] transition"
            >
              <X class="w-4 h-4" />
            </button>
          </div>

          <button
            @click="toggleToday"
            :aria-pressed="isTodayActive"
            :class="[
              'flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition whitespace-nowrap',
              isTodayActive
                ? 'bg-[#2A2018] border-[#D4956A] text-[#D4956A]'
                : 'bg-[#111113] border-[#2A2018] text-[#F5F0EA] hover:bg-[#1E1E23]',
            ]"
          >
            <CalendarDays class="w-4 h-4" />
            {{ t('visits.today') }}
          </button>

          <input
            v-model="dateFrom"
            type="date"
            :aria-label="t('visits.date_from')"
            :title="t('visits.date_from')"
            class="date-input w-auto px-3 py-2.5 bg-[#111113] border border-[#2A2018] rounded-xl text-sm text-[#F5F0EA] focus:outline-none focus:ring-2 focus:ring-teal-400 transition"
          />
          <input
            v-model="dateTo"
            type="date"
            :aria-label="t('visits.date_to')"
            :title="t('visits.date_to')"
            class="date-input w-auto px-3 py-2.5 bg-[#111113] border border-[#2A2018] rounded-xl text-sm text-[#F5F0EA] focus:outline-none focus:ring-2 focus:ring-teal-400 transition"
          />
        </div>

        <!-- Error -->
        <div
          v-if="error"
          class="mb-6 p-4 bg-red-900/20 border border-red-800 rounded-xl text-red-400"
        >
          {{ error }}
        </div>

        <!-- Table container -->
        <div class="bg-[#111113] rounded-xl border border-[#2A2018] overflow-hidden">
          <!-- Desktop Table -->
          <div class="hidden md:block overflow-x-auto">
            <table class="w-full">
              <thead class="bg-[#1E1E23] border-b border-[#2A2018]">
                <tr>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-[#524840] uppercase tracking-wider"
                  >
                    {{ t('visits.client') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-[#524840] uppercase tracking-wider"
                  >
                    {{ t('visits.services') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-[#524840] uppercase tracking-wider"
                  >
                    {{ t('visits.visit_at') }}
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-[#524840] uppercase tracking-wider"
                  >
                    {{ t('visits.user') }}
                  </th>
                  <th
                    class="px-6 py-3 text-right text-xs font-medium text-[#524840] uppercase tracking-wider"
                  >
                    {{ t('visits.actions') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-[#2A2018]">
                <tr v-if="isLoading && currentPage === 1">
                  <td colspan="5" class="px-6 py-12 text-center text-[#524840]">
                    {{ t('visits.loading') }}
                  </td>
                </tr>
                <tr v-else-if="visits.length === 0">
                  <td colspan="5" class="px-6 py-12 text-center text-[#524840]">
                    {{ t('visits.empty') }}
                  </td>
                </tr>
                <tr
                  v-for="visit in visits"
                  :key="visit.id"
                  @click="router.push(`/visits/${visit.id}`)"
                  class="hover:bg-[#1E1E23] transition cursor-pointer"
                >
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center gap-3">
                      <div
                        class="w-8 h-8 bg-teal-900/30 rounded-full flex items-center justify-center flex-shrink-0"
                      >
                        <span class="text-teal-300 text-xs font-medium">
                          {{ visit.client?.name[0]?.toUpperCase() || '?' }}
                        </span>
                      </div>
                      <span class="text-sm font-medium text-[#F5F0EA]">
                        {{ visit.client?.name || '—' }}
                      </span>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-[#524840]">
                    {{ serviceNames(visit) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-[#F5F0EA]">
                    {{ formatVisitAt(visit.visit_at) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-[#524840]">
                    {{ userName(visit) }}
                  </td>
                  <!-- `.stop` throughout: the whole row navigates to the detail view. -->
                  <td class="px-6 py-4 whitespace-nowrap text-right">
                    <div v-if="isUpcoming(visit)" class="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        :title="t('visits.reschedule')"
                        class="p-2 text-[#9E9080] hover:text-[#D4956A] hover:bg-[#1E1E23] rounded-lg transition"
                        @click.stop="rescheduleTarget = visit"
                      >
                        <CalendarClock class="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        :title="t('visits.cancel_visit')"
                        class="p-2 text-[#9E9080] hover:text-red-400 hover:bg-[#1E1E23] rounded-lg transition"
                        @click.stop="cancelTarget = visit"
                      >
                        <Ban class="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile Cards -->
          <div class="md:hidden divide-y divide-[#2A2018]">
            <div v-if="isLoading && currentPage === 1" class="p-6 text-center text-[#524840]">
              {{ t('visits.loading') }}
            </div>
            <div v-else-if="visits.length === 0" class="p-6 text-center text-[#524840]">
              {{ t('visits.empty') }}
            </div>
            <div
              v-for="visit in visits"
              :key="visit.id"
              @click="router.push(`/visits/${visit.id}`)"
              class="p-4 hover:bg-[#1E1E23] transition cursor-pointer"
            >
              <div class="flex items-start gap-3">
                <div
                  class="w-10 h-10 bg-teal-900/30 rounded-full flex items-center justify-center flex-shrink-0"
                >
                  <span class="text-teal-300 text-sm font-medium">
                    {{ visit.client?.name[0]?.toUpperCase() || '?' }}
                  </span>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-sm font-medium text-[#F5F0EA] truncate">
                    {{ visit.client?.name || '—' }}
                  </h3>
                  <p class="text-sm text-[#524840] mt-0.5">
                    {{ serviceNames(visit) }}
                  </p>
                  <p class="text-sm text-[#F5F0EA] mt-1">
                    {{ formatVisitAt(visit.visit_at) }}
                  </p>
                  <p class="text-xs text-[#9E9080] mt-1">
                    {{ userName(visit) }}
                  </p>
                  <!-- `.stop` throughout: the whole card navigates to the detail view. -->
                  <div v-if="isUpcoming(visit)" class="flex items-center gap-2 mt-3">
                    <button
                      type="button"
                      class="flex items-center gap-1.5 py-1.5 px-3 bg-[#1E1E23] hover:bg-[#2A2018] text-[#9E9080] rounded-lg text-xs font-medium transition"
                      @click.stop="rescheduleTarget = visit"
                    >
                      <CalendarClock class="w-3.5 h-3.5" />
                      {{ t('visits.reschedule') }}
                    </button>
                    <button
                      type="button"
                      class="flex items-center gap-1.5 py-1.5 px-3 bg-[#1E1E23] hover:bg-[#2A2018] text-red-400 rounded-lg text-xs font-medium transition"
                      @click.stop="cancelTarget = visit"
                    >
                      <Ban class="w-3.5 h-3.5" />
                      {{ t('visits.cancel_visit') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Load More -->
          <div v-if="hasMore && visits.length > 0" class="border-t border-[#2A2018] p-4">
            <button
              @click="handleLoadMore"
              :disabled="isLoading"
              class="w-full py-2.5 px-4 bg-[#1E1E23] hover:bg-[#2A2018] disabled:bg-[#111113] disabled:cursor-not-allowed text-[#9E9080] rounded-xl transition font-medium"
            >
              {{ isLoading ? t('visits.loading_more') : t('visits.load_more') }}
            </button>
          </div>

          <!-- No more -->
          <div
            v-if="!hasMore && visits.length > 0"
            class="border-t border-[#2A2018] p-4 text-center text-sm text-[#9E9080]"
          >
            {{ t('visits.no_more') }}
          </div>
        </div>
      </div>
    </div>

    <VisitRescheduleModal
      :open="rescheduleTarget !== null"
      :visit="rescheduleTarget"
      @close="rescheduleTarget = null"
    />

    <VisitCancelDialog :visit="cancelTarget" @close="cancelTarget = null" />
  </div>
</template>
